/**
 * theme.js — the app's light/dark theme controller, extracted from main.js so
 * Settings (General) can render a real switch alongside the nav-footer toggle.
 *
 * Policy (unchanged from main.js): always light on first visit — deliberately
 * NOT following prefers-color-scheme; dark only ever turns on via an explicit
 * user action. FF_SSE_UI_DARK_MODE is the kill switch (when off, stored 'dark'
 * is ignored and setTheme() no-ops).
 */

import PubSub from 'pubsub-js';
import AppBroadcast from '../../../../shared/appBroadcast.js';
import { FEATURE_FLAGS } from '../../../../shared/featureFlags.js';
import { darkModeEnabled, resolveInitialTheme } from './uiFlagGates.js';

/** Apply a theme by setting the attribute launCh.css's [data-theme="dark"]
 *  token overrides (and Tailwind's mirrored @theme layer) key off. */
export function applyTheme(theme) {
  document.documentElement.setAttribute('data-theme', theme);
}

/** Restore the persisted theme at boot (always light when the flag is off). */
export function initTheme() {
  applyTheme(resolveInitialTheme());
}

/** The currently applied theme. */
export function currentTheme() {
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
}

/** Set an explicit theme, persist it, and announce it. No-op when the
 *  FF_SSE_UI_DARK_MODE kill switch is off. */
export function setTheme(theme, { quiet = false } = {}) {
  if (!darkModeEnabled()) return;
  const next = theme === 'dark' ? 'dark' : 'light';
  if (next === currentTheme()) return;
  applyTheme(next);
  localStorage.setItem(FEATURE_FLAGS.FF_SSE_LS_THEME, next);
  if (!quiet) {
    PubSub.publish(AppBroadcast.UI_TOAST, { message: next === 'dark' ? 'Dark mode on' : 'Dark mode off', type: 'info' });
  }
  PubSub.publish(AppBroadcast.UI_THEME_CHANGED, { theme: next });
}

/** Flip light/dark (the nav-footer action). */
export function toggleTheme() {
  setTheme(currentTheme() === 'dark' ? 'light' : 'dark');
}
